import React, { ReactNode, useRef } from 'react';
import FillSpace from './VirtualizedList/FillSpace';
import useOffset from './VirtualizedList/useOffset';
import useScroll from './VirtualizedList/useScroll';

export interface VirtualizedListPropsType<T> {
  items: T[];
  renderItem: (item: T) => ReactNode;
  itemHeight: number;
  renderPlaceholder?: (item: T) => ReactNode;
  overScan?: number;
}

const VirtualizedList = <T extends any>({
  items,
  renderItem,
  renderPlaceholder,
  itemHeight,
  overScan = 3,
}: VirtualizedListPropsType<T>) => {
  const wrapperRef = useRef(null);
  const maxLoaded = Math.ceil(window.innerHeight / itemHeight) + overScan;
  const [offset, adjustOffset] = useOffset(
    0,
    Math.max(items.length - maxLoaded - overScan, 0),
  );
  const endIndex = offset + maxLoaded + overScan;
  const rCount = Math.max(items.length - endIndex, 0);

  useScroll(() =>
    adjustOffset({
      setTo:
        Math.floor(
          Math.max(
            window.pageYOffset -
              (wrapperRef!.current! as HTMLDivElement).offsetTop,
            0,
          ) / itemHeight,
        ) - overScan,
    }),
  );

  return (
    <div data-cy="requests-list" ref={wrapperRef}>
      <FillSpace count={offset} {...{ items, renderPlaceholder, itemHeight }} />
      {items.slice(offset, endIndex).map(renderItem)}
      <FillSpace count={rCount} {...{ items, renderPlaceholder, itemHeight }} />
    </div>
  );
};

export default VirtualizedList;
